<template>
    <dialog-window v-model="isOpen" data-id="editPasswordDialog" :title="title" @close="closeDialogHandler">
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section :is-loading="isLoading">
                <input-field
                    id="currentPassword"
                    v-model="formData.currentPassword"
                    data-id="editPasswordDialogCurrentPasswordField"
                    :type="FIELD_TYPES.PASSWORD"
                    class="pt-4 flex-1-0-0"
                    :required="true"
                    :pattern="FIELD_PATTERNS.CAPITAL_LETTER"
                    :disabled="isLoading"
                    :label="t('profile.currentPassword')"
                />
                <input-field
                    id="password"
                    v-model="formData.password"
                    data-id="editPasswordDialogNewPasswordField"
                    :type="FIELD_TYPES.PASSWORD"
                    class="pt-4 flex-1-0-0"
                    :required="true"
                    :pattern="FIELD_PATTERNS.CAPITAL_LETTER"
                    :disabled="isLoading"
                    :label="t('profile.newPassword')"
                />
                <input-field
                    id="passwordConfirm"
                    v-model="formData.passwordConfirm"
                    data-id="editPasswordDialogConfirmNewPasswordField"
                    :type="FIELD_TYPES.PASSWORD"
                    class="pt-4 flex-1-0-0"
                    :reference-value="formData.password"
                    :reference-error-label="t('register.passwordsMustMatch')"
                    :disabled="isLoading"
                    :required="true"
                    :label="t('profile.confirmNewPassword')"
                />

                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        data-id="editPasswordDialogCancelButton"
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        :disabled="isSubmitting"
                        @click="closeDialogHandler"
                    >
                        {{ t('profile.cancel') }}
                    </card-button>
                    <card-button
                        data-id="editPasswordDialogSubmitButton"
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        :loading="isLoading"
                        :inactive-label="t('inquiry.sendMessage')"
                        :disabled="isSubmitting"
                    >
                        {{ t('profile.changePassword') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { FormSection, InputField, NotificationBar } from '@/features/common/components/form';
import { FIELD_PATTERNS, FIELD_TYPES } from '@/features/common/components/form/constants';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import CardButton from '@/features/common/components/CardButton.vue';
import { IEditProfileDialog } from '../types';
import { useGlobalDialogStore } from '@/store/dialogStore';
import DialogWindow from '@/features/common/components/DialogWindow.vue';

const { t } = useI18n();
const container = useContainer();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const props = defineProps<IEditProfileDialog>();

const isOpen = computed(() => isDialogOpen(props.type));

const isLoading = ref(false);
interface IUpdatePassword {
    currentPassword: string;
    password: string;
    passwordConfirm: string;
}

const updatePassword: IUpdatePassword = {
    currentPassword: '',
    password: '',
    passwordConfirm: ''
};

const { form, isSubmitting, validateForm, toggleIsSubmitting, isFormEmpty } = useFormHandling();
const { notificationBarState, notificationError, notificationSuccess } = useNotification();
const { formData, resetFormData } = useFormData(updatePassword);

const saveData = async (data: IUpdatePassword) => {
    try {
        await profileApi.updatePassword({
            password: data.password,
            passwordConfirm: data.passwordConfirm,
            existingPassword: data.currentPassword
        });
        notificationBarState.value = notificationSuccess(t('profile.passwordChanged'));
        return true;
    } catch {
        notificationBarState.value = notificationError(t('profile.updateError'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        isLoading.value = true;
        const result = await saveData(formData.value);
        result && closeDialog(props.type);
    } finally {
        toggleIsSubmitting(false);
        isLoading.value = false;
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const closeDialogHandler = () => {
    resetFormData(form);
    closeDialog(props.type);
};
</script>
